import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import usePassengerService from '../../services/usePassengerService ';
import showToast from '../../utils/toast';
import {
  BULK_PASSENGER_MAX,
  BulkPassengerRow,
  BulkPassengerRowErrors,
  ExistingPassengerLike,
  isPlaceholderAddress,
  normalizeStreet,
  pickPreferredAddress,
  validateBulkRows,
} from './bulkPassengerValidation';
import './styles/BulkPassengerModal.css';

export type BulkPassengerCreated = {
  id: number;
  name: string;
  first_lastname?: string;
  second_lastname?: string;
  phone?: string;
  /** Dirección ingresada en el alta masiva (fuente de verdad para la ruta). */
  street: string;
  addresses: { id: number; street: string }[];
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  /** Empresa fija (formulario de servicio). Si no se envía, se muestra selector. */
  companyId?: number | string | null;
  companyLabel?: string | null;
  companies?: { value: string; label: string }[];
  existingPassengers?: ExistingPassengerLike[];
  /** Filas iniciales (p. ej. desde importación El Abra). */
  initialRows?: BulkPassengerRow[];
  title?: string;
  subtitle?: string;
  /** Eleva el z-index para abrir encima de otro modal. */
  stacked?: boolean;
  onSuccess?: (created: BulkPassengerCreated[]) => void;
};

const emptyRow = (): BulkPassengerRow => ({
  name: '',
  first_lastname: '',
  second_lastname: '',
  phone: '',
  street: '',
});

const BulkPassengerModal: React.FC<Props> = ({
  isOpen,
  onClose,
  companyId: fixedCompanyId,
  companyLabel,
  companies = [],
  existingPassengers = [],
  initialRows,
  title = 'Agregar pasajeros',
  subtitle,
  stacked = false,
  onSuccess,
}) => {
  const { store, byCompany, listAddress } = usePassengerService();
  const needsCompanySelect = fixedCompanyId == null || fixedCompanyId === '' || fixedCompanyId === 0;

  const [quantity, setQuantity] = useState(1);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [rows, setRows] = useState<BulkPassengerRow[]>([emptyRow()]);
  const [rowErrors, setRowErrors] = useState<BulkPassengerRowErrors[]>([{}]);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [companyPassengers, setCompanyPassengers] = useState<ExistingPassengerLike[]>([]);

  const resolvedCompanyId = useMemo(() => {
    if (!needsCompanySelect) return String(fixedCompanyId);
    return selectedCompany;
  }, [needsCompanySelect, fixedCompanyId, selectedCompany]);

  const resolvedCompanyLabel = useMemo(() => {
    if (companyLabel) return companyLabel;
    const found = companies.find((c) => c.value === resolvedCompanyId);
    return found?.label ?? '';
  }, [companyLabel, companies, resolvedCompanyId]);

  const lockQuantity = Boolean(initialRows?.length);

  useEffect(() => {
    if (!isOpen) return;
    const seed = initialRows?.length ? initialRows.map((row) => ({ ...row })) : [emptyRow()];
    setQuantity(seed.length);
    setRows(seed);
    setRowErrors(seed.map(() => ({})));
    setGlobalError(null);
    setCompanyPassengers([]);
    if (needsCompanySelect) {
      setSelectedCompany('');
    }
  }, [isOpen, needsCompanySelect, initialRows]);

  useEffect(() => {
    if (!isOpen) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !resolvedCompanyId) {
      setCompanyPassengers([]);
      return;
    }
    byCompany(Number(resolvedCompanyId), {
      onSuccess: (resp: any) => {
        const list = Array.isArray(resp?.data) ? resp.data : [];
        setCompanyPassengers(list);
      },
      onError: () => setCompanyPassengers([]),
    });
  }, [isOpen, resolvedCompanyId, byCompany]);

  const duplicateCheckList = useMemo(() => {
    if (companyPassengers.length > 0) return companyPassengers;
    return existingPassengers;
  }, [companyPassengers, existingPassengers]);

  const progressText = useMemo(() => {
    const completed = rows.filter(
      (r) =>
        (r.name || '').trim() &&
        (r.first_lastname || '').trim() &&
        (r.street || '').trim()
    ).length;
    return `${completed}/${rows.length} completos`;
  }, [rows]);

  useEffect(() => {
    if (!isOpen) return;
    const n = Math.min(Math.max(1, quantity), BULK_PASSENGER_MAX);
    setRows((prev) => {
      if (prev.length === n) return prev;
      if (prev.length < n) {
        return [...prev, ...Array.from({ length: n - prev.length }, emptyRow)];
      }
      return prev.slice(0, n);
    });
    setRowErrors((prev) => {
      if (prev.length === n) return prev;
      if (prev.length < n) {
        return [...prev, ...Array.from({ length: n - prev.length }, () => ({}))];
      }
      return prev.slice(0, n);
    });
  }, [quantity, isOpen]);

  const updateRow = useCallback((index: number, field: keyof BulkPassengerRow, value: string) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
    setRowErrors((prev) =>
      prev.map((err, i) => {
        if (i !== index) return err;
        const next = { ...err };
        delete next[field];
        delete next._row;
        return next;
      })
    );
    setGlobalError(null);
  }, []);

  const handleSave = async () => {
    if (!resolvedCompanyId) {
      setGlobalError('Selecciona una empresa antes de guardar.');
      return;
    }

    const validation = validateBulkRows(rows, duplicateCheckList);
    setRowErrors(validation);

    const hasErrors = validation.some(
      (e) => Object.keys(e).length > 0
    );
    if (hasErrors) {
      setGlobalError('Revisa los campos marcados y corrige duplicados antes de guardar.');
      return;
    }

    setSaving(true);
    setGlobalError(null);

    const created: BulkPassengerCreated[] = [];
    const failed: { index: number; message: string }[] = [];

    for (let i = 0; i < rows.length; i += 1) {
      const row = rows[i];
      try {
        const response = (await store({
          company: Number(resolvedCompanyId),
          name: row.name.trim(),
          first_lastname: row.first_lastname.trim(),
          second_lastname: row.second_lastname.trim() || undefined,
          phone: row.phone.trim() || undefined,
          email: undefined,
          addresses: [{ street: row.street.trim() }],
          also_as_requester: false,
          also_as_authorizer: false,
        } as any)) as { success?: boolean; data?: { id?: number }; message?: string; errors?: any; http_status?: number } | void;

        const httpStatus = response?.http_status ?? 0;
        const failedRequest =
          !response ||
          response.success === false ||
          (httpStatus >= 400 && httpStatus !== 0);

        if (failedRequest) {
          const nameErr =
            response?.errors?.name ??
            response?.errors?.first_lastname ??
            response?.message;
          failed.push({
            index: i,
            message: Array.isArray(nameErr) ? nameErr[0] : nameErr || 'No se pudo crear',
          });
          continue;
        }

        const id = response?.data?.id;
        if (!id) {
          failed.push({ index: i, message: 'Respuesta sin ID de pasajero' });
          continue;
        }

        const enteredStreet = normalizeStreet(row.street);
        let addresses: BulkPassengerCreated['addresses'] = [{ id: 0, street: enteredStreet }];
        const addrResp = (await listAddress(id)) as {
          success?: boolean;
          data?: { id: number; street: string }[];
          http_status?: number;
        } | void;
        if (
          addrResp &&
          addrResp.success !== false &&
          Array.isArray(addrResp.data) &&
          addrResp.data.length > 0
        ) {
          const fromApi = addrResp.data.map((a) => ({
            id: a.id,
            street: a.street,
          }));
          const preferred = pickPreferredAddress(fromApi, enteredStreet);
          if (preferred) {
            addresses = [preferred];
          } else if (!isPlaceholderAddress(enteredStreet)) {
            addresses = [{ id: 0, street: enteredStreet }];
          }
        }

        created.push({
          id,
          name: row.name.trim(),
          first_lastname: row.first_lastname.trim(),
          second_lastname: row.second_lastname.trim() || undefined,
          phone: row.phone.trim() || undefined,
          street: enteredStreet,
          addresses,
        });
      } catch {
        failed.push({ index: i, message: 'Error de conexión' });
      }
    }

    setSaving(false);

    if (failed.length > 0) {
      setRowErrors((prev) => {
        const next = [...prev];
        failed.forEach(({ index, message }) => {
          next[index] = { ...next[index], _row: message };
        });
        return next;
      });
      if (created.length > 0) {
        showToast(
          `Se crearon ${created.length} pasajero(s); ${failed.length} con error.`,
          'info'
        );
        onSuccess?.(created);
      } else {
        setGlobalError('No se pudo guardar ningún pasajero. Revisa los errores en cada fila.');
        return;
      }
    } else {
      showToast(
        created.length === 1
          ? 'Pasajero creado correctamente'
          : `${created.length} pasajeros creados correctamente`,
        'success'
      );
      onSuccess?.(created);
    }

    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className={`bulk-passenger-overlay${stacked ? " bulk-passenger-overlay--stacked" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="bulk-passenger-title"
      onClick={onClose}
    >
      <div className="bulk-passenger-modal" onClick={(e) => e.stopPropagation()}>
        <header className="bulk-passenger-header">
          <div>
            <h3 id="bulk-passenger-title" className="bulk-passenger-title">
              {title}
            </h3>
            <p className="bulk-passenger-subtitle">
              {subtitle ??
                `Alta rápida por cantidad (máximo ${BULK_PASSENGER_MAX}) con dirección por cada pasajero.`}
            </p>
            {!needsCompanySelect && resolvedCompanyLabel && (
              <span className="bulk-passenger-company-badge">{resolvedCompanyLabel}</span>
            )}
            <p className="bulk-passenger-progress" aria-live="polite">
              {progressText}
            </p>
          </div>
          <button
            type="button"
            className="bulk-passenger-close"
            onClick={onClose}
            aria-label="Cerrar"
            disabled={saving}
          >
            ×
          </button>
        </header>

        <div className="bulk-passenger-body">
          {globalError && (
            <p className="bulk-passenger-global-error" role="alert">
              {globalError}
            </p>
          )}

          <div className="bulk-passenger-setup">
            {needsCompanySelect && (
              <div className="bulk-passenger-setup__field">
                <label htmlFor="bulk-passenger-company">Empresa</label>
                <select
                  id="bulk-passenger-company"
                  value={selectedCompany}
                  onChange={(e) => {
                    setSelectedCompany(e.target.value);
                    setGlobalError(null);
                  }}
                  disabled={saving}
                >
                  <option value="">Seleccionar empresa</option>
                  {companies.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="bulk-passenger-setup__field">
              <label htmlFor="bulk-passenger-qty">Cantidad</label>
              <div className="bulk-passenger-qty-control" role="group" aria-label="Seleccionar cantidad de pasajeros">
                <button
                  type="button"
                  className="bulk-passenger-qty-btn"
                  onClick={() => setQuantity((prev) => Math.max(1, prev - 1))}
                  disabled={saving || quantity <= 1 || lockQuantity}
                  aria-label="Disminuir cantidad"
                >
                  -
                </button>
                <input
                  id="bulk-passenger-qty"
                  type="number"
                  min={1}
                  max={BULK_PASSENGER_MAX}
                  value={quantity}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    if (Number.isNaN(v)) return;
                    setQuantity(Math.min(BULK_PASSENGER_MAX, Math.max(1, v)));
                  }}
                  disabled={saving || lockQuantity}
                />
                <button
                  type="button"
                  className="bulk-passenger-qty-btn"
                  onClick={() => setQuantity((prev) => Math.min(BULK_PASSENGER_MAX, prev + 1))}
                  disabled={saving || quantity >= BULK_PASSENGER_MAX || lockQuantity}
                  aria-label="Aumentar cantidad"
                >
                  +
                </button>
              </div>
              <p className="bulk-passenger-setup__hint">
                {lockQuantity
                  ? 'Cantidad fijada por la selección de la planilla.'
                  : `Entre 1 y ${BULK_PASSENGER_MAX}`}
              </p>
            </div>
          </div>

          <div className="bulk-passenger-table-wrap">
            <table className="bulk-passenger-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Nombre</th>
                  <th>Apellido</th>
                  <th>Segundo apellido</th>
                  <th>Celular</th>
                  <th>Dirección</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => {
                  const errs = rowErrors[index] ?? {};
                  return (
                    <tr key={index}>
                      <td className="bulk-passenger-index">{index + 1}</td>
                      <td>
                        <input
                          type="text"
                          value={row.name}
                          onChange={(e) => updateRow(index, 'name', e.target.value)}
                          className={errs.name ? 'has-error' : ''}
                          autoComplete="off"
                          disabled={saving}
                        />
                        {errs.name && (
                          <div className="bulk-passenger-row-error">{errs.name}</div>
                        )}
                      </td>
                      <td>
                        <input
                          type="text"
                          value={row.first_lastname}
                          onChange={(e) => updateRow(index, 'first_lastname', e.target.value)}
                          className={errs.first_lastname ? 'has-error' : ''}
                          autoComplete="off"
                          disabled={saving}
                        />
                        {errs.first_lastname && (
                          <div className="bulk-passenger-row-error">{errs.first_lastname}</div>
                        )}
                      </td>
                      <td>
                        <input
                          type="text"
                          value={row.second_lastname}
                          onChange={(e) => updateRow(index, 'second_lastname', e.target.value)}
                          autoComplete="off"
                          disabled={saving}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={row.phone}
                          onChange={(e) => updateRow(index, 'phone', e.target.value)}
                          autoComplete="off"
                          disabled={saving}
                        />
                        {errs._row && (
                          <div className="bulk-passenger-row-error">{errs._row}</div>
                        )}
                      </td>
                      <td>
                        <input
                          type="text"
                          value={row.street}
                          onChange={(e) => updateRow(index, 'street', e.target.value)}
                          className={errs.street ? 'has-error' : ''}
                          autoComplete="off"
                          placeholder="Ej. Av. Balmaceda 123 | N/A"
                          required
                          aria-required="true"
                          disabled={saving}
                        />
                        {errs.street && (
                          <div className="bulk-passenger-row-error">{errs.street}</div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <footer className="bulk-passenger-footer">
          <button
            type="button"
            className="bulk-passenger-btn bulk-passenger-btn--ghost"
            onClick={onClose}
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="bulk-passenger-btn bulk-passenger-btn--primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Guardando…' : 'Guardar pasajeros'}
          </button>
        </footer>
      </div>
    </div>,
    document.body
  );
};

export default BulkPassengerModal;
